import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  Plus,
  Trash2,
  Loader2,
  CheckCircle2,
  AlertCircle,
  Eye,
  EyeOff,
  ArrowUp,
  ArrowDown,
  ChevronDown,
  ChevronRight,
} from 'lucide-react'
import { Topbar } from '../components/Topbar'
import { Badge } from '../components/Badge'
import { apiGet, apiPost, apiPut, apiDelete } from '../lib/api'

type HomeSection = {
  id: string
  sectionKey: string
  type: SectionType
  title: string
  subtitle: string
  ctaText: string
  ctaUrl: string
  image: string
  configJson: unknown
  sortOrder: number
  isActive: boolean
}

type SectionType =
  | 'hero'
  | 'benefits'
  | 'featured'
  | 'week_sale'
  | 'categories'
  | 'brand_carousel'
  | 'testimonials'
  | 'newsletter'
  | 'cta_band'

const TYPE_LABELS: Record<SectionType, string> = {
  hero: 'Slider hero',
  benefits: 'Banda de beneficios',
  featured: 'Productos destacados',
  week_sale: 'Ofertas de la semana',
  categories: 'Categorías destacadas',
  brand_carousel: 'Carrusel de marcas',
  testimonials: 'Testimonios',
  newsletter: 'Newsletter',
  cta_band: 'Banda CTA',
}

const TYPES = Object.keys(TYPE_LABELS) as SectionType[]

export function SeccionesHome() {
  const qc = useQueryClient()
  const [flash, setFlash] = useState<{ tipo: 'ok' | 'err'; msg: string } | null>(null)
  const [expanded, setExpanded] = useState<Set<string>>(new Set())
  const [adding, setAdding] = useState(false)
  const [draft, setDraft] = useState({ section_key: '', type: 'cta_band' as SectionType })

  const { data, isLoading } = useQuery({
    queryKey: ['home-sections'],
    queryFn: () => apiGet<{ data: HomeSection[] }>('/api/admin/home-sections'),
  })

  const items = data?.data ?? []

  function notify(tipo: 'ok' | 'err', msg: string) {
    setFlash({ tipo, msg })
    setTimeout(() => setFlash(null), 3000)
  }

  function refresh() {
    qc.invalidateQueries({ queryKey: ['home-sections'] })
  }

  function toggle(id: string) {
    setExpanded((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const createMut = useMutation({
    mutationFn: (b: { section_key: string; type: SectionType; sort_order: number }) =>
      apiPost<HomeSection>('/api/admin/home-sections', b),
    onSuccess: (s) => {
      notify('ok', 'Sección creada')
      setAdding(false)
      setDraft({ section_key: '', type: 'cta_band' })
      setExpanded((prev) => new Set(prev).add(s.id))
      refresh()
    },
    onError: (e: Error) => notify('err', e.message),
  })

  const updateMut = useMutation({
    mutationFn: ({ id, ...body }: { id: string } & Partial<HomeSection>) => {
      const payload: Record<string, unknown> = {}
      if (body.sectionKey !== undefined) payload.section_key = body.sectionKey
      if (body.type !== undefined) payload.type = body.type
      if (body.title !== undefined) payload.title = body.title
      if (body.subtitle !== undefined) payload.subtitle = body.subtitle
      if (body.ctaText !== undefined) payload.cta_text = body.ctaText
      if (body.ctaUrl !== undefined) payload.cta_url = body.ctaUrl
      if (body.image !== undefined) payload.image = body.image
      if (body.sortOrder !== undefined) payload.sort_order = body.sortOrder
      if (body.isActive !== undefined) payload.is_active = body.isActive
      if (body.configJson !== undefined) payload.config_json = body.configJson
      return apiPut<HomeSection>(`/api/admin/home-sections/${id}`, payload)
    },
    onSuccess: () => refresh(),
    onError: (e: Error) => notify('err', e.message),
  })

  const deleteMut = useMutation({
    mutationFn: (id: string) => apiDelete<{ ok: true }>(`/api/admin/home-sections/${id}`),
    onSuccess: () => {
      notify('ok', 'Sección eliminada')
      refresh()
    },
    onError: (e: Error) => notify('err', e.message),
  })

  function move(item: HomeSection, dir: -1 | 1) {
    const idx = items.findIndex((i) => i.id === item.id)
    const swap = items[idx + dir]
    if (!swap) return
    updateMut.mutate({ id: item.id, sortOrder: swap.sortOrder })
    updateMut.mutate({ id: swap.id, sortOrder: item.sortOrder })
  }

  function confirmAdd() {
    const key = draft.section_key.trim()
    if (!key) {
      notify('err', 'Falta la clave (ej. home_promo)')
      return
    }
    if (!/^[a-z0-9_]+$/.test(key)) {
      notify('err', 'La clave solo admite letras minúsculas, números y _')
      return
    }
    const nextOrder = (items.at(-1)?.sortOrder ?? 0) + 1
    createMut.mutate({ section_key: key, type: draft.type, sort_order: nextOrder })
  }

  return (
    <>
      <Topbar
        title="Secciones del home"
        subtitle="Cada banda del home pública se gestiona aquí: heading, subheading, CTA, imagen y orden"
        actions={
          <>
            {data && <Badge variant="info">{items.length} secciones</Badge>}
            <button
              type="button"
              onClick={() => setAdding(true)}
              className="inline-flex items-center gap-1.5 rounded-lg bg-brand-600 px-3 py-1.5 text-sm font-semibold text-white hover:bg-brand-700"
            >
              <Plus className="size-3.5" />
              Nueva sección
            </button>
          </>
        }
      />

      {flash && (
        <div
          className={`mx-6 mt-4 flex items-center gap-2 rounded-lg border px-3 py-2 text-sm ${
            flash.tipo === 'ok'
              ? 'border-emerald-200 bg-emerald-50 text-emerald-800'
              : 'border-red-200 bg-red-50 text-red-800'
          }`}
        >
          {flash.tipo === 'ok' ? (
            <CheckCircle2 className="size-4" />
          ) : (
            <AlertCircle className="size-4" />
          )}
          <span>{flash.msg}</span>
        </div>
      )}

      <div className="p-6">
        {isLoading ? (
          <div className="flex items-center gap-2 text-ink-500">
            <Loader2 className="size-4 animate-spin" /> Cargando secciones…
          </div>
        ) : (
          <div className="space-y-2">
            {items.map((item, idx) => {
              const isExp = expanded.has(item.id)
              return (
                <div
                  key={item.id}
                  className="overflow-hidden rounded-2xl border border-ink-200 bg-white"
                >
                  <div className="flex items-center gap-2 p-3">
                    <button
                      type="button"
                      onClick={() => toggle(item.id)}
                      className="grid size-7 place-items-center rounded text-ink-500 hover:bg-ink-100"
                    >
                      {isExp ? (
                        <ChevronDown className="size-4" />
                      ) : (
                        <ChevronRight className="size-4" />
                      )}
                    </button>

                    <Badge variant="default">{TYPE_LABELS[item.type]}</Badge>

                    <div className="font-mono text-xs text-ink-500">{item.sectionKey}</div>

                    <div className="flex-1 truncate text-sm font-medium text-ink-900">
                      {item.title || <span className="italic text-ink-400">(sin título)</span>}
                    </div>

                    <button
                      type="button"
                      title={item.isActive ? 'Ocultar' : 'Mostrar'}
                      onClick={() =>
                        updateMut.mutate({ id: item.id, isActive: !item.isActive })
                      }
                      className="grid size-8 place-items-center rounded text-ink-500 hover:bg-ink-100"
                    >
                      {item.isActive ? (
                        <Eye className="size-4 text-emerald-600" />
                      ) : (
                        <EyeOff className="size-4 text-ink-400" />
                      )}
                    </button>

                    <div className="flex items-center">
                      <button
                        type="button"
                        disabled={idx === 0}
                        onClick={() => move(item, -1)}
                        className="grid size-8 place-items-center rounded text-ink-500 hover:bg-ink-100 disabled:opacity-30"
                      >
                        <ArrowUp className="size-4" />
                      </button>
                      <button
                        type="button"
                        disabled={idx === items.length - 1}
                        onClick={() => move(item, 1)}
                        className="grid size-8 place-items-center rounded text-ink-500 hover:bg-ink-100 disabled:opacity-30"
                      >
                        <ArrowDown className="size-4" />
                      </button>
                    </div>

                    <button
                      type="button"
                      title="Eliminar"
                      onClick={() => {
                        if (
                          confirm(
                            `Eliminar la sección "${item.sectionKey}"? El home dejará de mostrarla.`,
                          )
                        )
                          deleteMut.mutate(item.id)
                      }}
                      className="grid size-8 place-items-center rounded text-red-500 hover:bg-red-50"
                    >
                      <Trash2 className="size-4" />
                    </button>
                  </div>

                  {isExp && (
                    <div className="space-y-3 border-t border-ink-100 bg-ink-50/40 p-4">
                      <div className="grid gap-3 md:grid-cols-2">
                        <Field label="Título">
                          <input
                            type="text"
                            value={item.title}
                            placeholder='Ej. "Más vendidos"'
                            onChange={(e) =>
                              updateMut.mutate({ id: item.id, title: e.target.value })
                            }
                            className="w-full rounded-lg border border-ink-200 bg-white px-3 py-2 text-sm outline-none focus:border-brand-500"
                          />
                        </Field>
                        <Field label="Subtítulo">
                          <input
                            type="text"
                            value={item.subtitle}
                            placeholder="Texto secundario opcional"
                            onChange={(e) =>
                              updateMut.mutate({ id: item.id, subtitle: e.target.value })
                            }
                            className="w-full rounded-lg border border-ink-200 bg-white px-3 py-2 text-sm outline-none focus:border-brand-500"
                          />
                        </Field>
                        <Field label="Texto del botón (CTA)">
                          <input
                            type="text"
                            value={item.ctaText}
                            placeholder='Ej. "Ver más"'
                            onChange={(e) =>
                              updateMut.mutate({ id: item.id, ctaText: e.target.value })
                            }
                            className="w-full rounded-lg border border-ink-200 bg-white px-3 py-2 text-sm outline-none focus:border-brand-500"
                          />
                        </Field>
                        <Field label="URL del botón (CTA)">
                          <input
                            type="url"
                            value={item.ctaUrl}
                            placeholder="/catalogo  o  https://…"
                            onChange={(e) =>
                              updateMut.mutate({ id: item.id, ctaUrl: e.target.value })
                            }
                            className="w-full rounded-lg border border-ink-200 bg-white px-3 py-2 font-mono text-xs outline-none focus:border-brand-500"
                          />
                        </Field>
                        <Field label="Imagen (opcional)">
                          <div className="flex items-center gap-3">
                            <div className="grid size-16 shrink-0 place-items-center overflow-hidden rounded-lg border border-ink-200 bg-white">
                              {item.image ? (
                                <img
                                  src={item.image}
                                  alt={item.title}
                                  className="max-h-full max-w-full object-contain"
                                  onError={(e) => {
                                    ;(e.target as HTMLImageElement).style.display = 'none'
                                  }}
                                />
                              ) : (
                                <span className="text-[10px] text-ink-400">vacío</span>
                              )}
                            </div>
                            <input
                              type="url"
                              value={item.image}
                              placeholder="/ruta/imagen.png o https://…"
                              onChange={(e) =>
                                updateMut.mutate({ id: item.id, image: e.target.value })
                              }
                              className="flex-1 rounded-lg border border-ink-200 bg-white px-3 py-2 font-mono text-xs outline-none focus:border-brand-500"
                            />
                          </div>
                        </Field>
                        <Field label="Tipo">
                          <select
                            value={item.type}
                            onChange={(e) =>
                              updateMut.mutate({
                                id: item.id,
                                type: e.target.value as SectionType,
                              })
                            }
                            className="w-full rounded-lg border border-ink-200 bg-white px-3 py-2 text-sm outline-none focus:border-brand-500"
                          >
                            {TYPES.map((t) => (
                              <option key={t} value={t}>
                                {TYPE_LABELS[t]}
                              </option>
                            ))}
                          </select>
                        </Field>
                      </div>
                    </div>
                  )}
                </div>
              )
            })}

            {adding && (
              <div className="flex items-center gap-3 rounded-2xl border border-dashed border-brand-300 bg-brand-50 p-3">
                <input
                  type="text"
                  autoFocus
                  value={draft.section_key}
                  placeholder="Clave única (ej. home_promo)"
                  onChange={(e) =>
                    setDraft((d) => ({ ...d, section_key: e.target.value }))
                  }
                  className="flex-1 rounded-md border border-ink-200 bg-white px-3 py-2 font-mono text-xs outline-none focus:border-brand-500"
                />
                <select
                  value={draft.type}
                  onChange={(e) =>
                    setDraft((d) => ({ ...d, type: e.target.value as SectionType }))
                  }
                  className="rounded-md border border-ink-200 bg-white px-2 py-2 text-xs"
                >
                  {TYPES.map((t) => (
                    <option key={t} value={t}>
                      {TYPE_LABELS[t]}
                    </option>
                  ))}
                </select>
                <button
                  type="button"
                  onClick={confirmAdd}
                  className="rounded-md bg-brand-600 px-3 py-2 text-xs font-semibold text-white hover:bg-brand-700"
                >
                  Crear
                </button>
                <button
                  type="button"
                  onClick={() => setAdding(false)}
                  className="rounded-md border border-ink-200 bg-white px-3 py-2 text-xs text-ink-600 hover:bg-ink-100"
                >
                  Cancelar
                </button>
              </div>
            )}

            {!isLoading && items.length === 0 && !adding && (
              <div className="rounded-2xl border border-dashed border-ink-200 bg-white p-10 text-center">
                <h3 className="text-sm font-semibold text-ink-900">Sin secciones</h3>
                <p className="mt-1 text-xs text-ink-500">
                  Crea la primera sección con el botón de arriba.
                </p>
              </div>
            )}
          </div>
        )}
      </div>
    </>
  )
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="mb-1 block text-xs font-semibold text-ink-600">{label}</span>
      {children}
    </label>
  )
}
